import { Link, useLocation } from 'wouter';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  Plus, 
  User,
  Wrench,
  FileText,
  Clock,
  Building
} from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { useIsMobile } from '@/shared/hooks/use-mobile';
import type { User as UserType } from '@shared/schema/types';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  brands?: string[];
  description?: string;
}

export default function BottomNavigationBar() {
  // All hooks must be called before any early returns
  const [location] = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { currentBrand } = useBranding();
  const isMobile = useIsMobile();

  // Don't render on desktop
  if (!isMobile) return null;

  // Ensure currentBrand is available before proceeding
  if (!currentBrand) return null;

  const userRole = isAuthenticated && user ? (user as UserType).role : null;

  // Define bottom navigation items based on role and brand
  const getNavigationItems = (): NavItem[] => {
    const commonItems: NavItem[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        href: '/dashboard',
        icon: LayoutDashboard,
        description: 'Personal dashboard overview'
      }
    ];

    // Role-specific second item
    const scheduleItems: NavItem[] = [];
    if (userRole === 'technician') {
      scheduleItems.push({
        id: 'assignments',
        label: 'Assignments',
        href: '/field-technician',
        icon: Clock,
        roles: ['technician'],
        description: 'Daily assignments and tasks'
      });
    } else if (userRole === 'business_owner' || userRole === 'admin') {
      scheduleItems.push({
        id: 'schedule',
        label: 'Schedule',
        href: '/predictive-calendar',
        icon: Calendar,
        roles: ['business_owner', 'admin'],
        description: 'View and manage appointments'
      });
    } else {
      // Default to schedule for unauthenticated users
      scheduleItems.push({
        id: 'schedule',
        label: 'Services',
        href: '/services',
        icon: Wrench,
        description: 'Service management'
      });
    }

    // Business operations item
    const businessItems: NavItem[] = [];
    if (userRole === 'business_owner' || userRole === 'admin') {
      if (currentBrand?.type === 'greenshield') {
        businessItems.push({
          id: 'tenders',
          label: 'Tenders',
          href: '/tenders',
          icon: FileText,
          roles: ['business_owner', 'admin'],
          brands: ['greenshield'],
          description: 'Tender management'
        });
      } else {
        businessItems.push({
          id: 'crm',
          label: 'CRM',
          href: '/crm',
          icon: Building,
          roles: ['business_owner', 'admin'],
          description: 'Customer relationship management'
        });
      }
    } else {
      businessItems.push({
        id: 'customers',
        label: 'Customers',
        href: '/customer-management',
        icon: Users,
        description: 'Customer management'
      });
    }

    // Quick actions (always show for authenticated users)
    const quickActionItems: NavItem[] = [];
    if (isAuthenticated) {
      if (userRole === 'technician') {
        quickActionItems.push({
          id: 'quick-action',
          label: 'New Service',
          href: '/services?action=create',
          icon: Plus,
          roles: ['technician'],
          description: 'Create new service order'
        });
      } else {
        quickActionItems.push({
          id: 'quick-action',
          label: 'Quick Add',
          href: '/customer-management?action=create',
          icon: Plus,
          roles: ['business_owner', 'admin'],
          description: 'Quick actions menu'
        });
      }
    }

    // Profile (always last)
    const profileItems: NavItem[] = [
      {
        id: 'profile',
        label: 'Profile',
        href: '/profile',
        icon: User,
        description: 'Account settings and profile'
      }
    ];

    return [
      ...commonItems,
      ...scheduleItems,
      ...businessItems,
      ...quickActionItems,
      ...profileItems
    ].slice(0, 5); // Limit to 5 items max
  };

  // Helper function to check if user has access to an item
  const hasAccess = (item: NavItem) => {
    // Check brand access
    if (item.brands && currentBrand?.type && !item.brands.includes(currentBrand.type)) {
      return false;
    }
    
    // Check role access for authenticated users
    if (isAuthenticated && user && item.roles) {
      return item.roles.includes(userRole!);
    }
    
    // If no role restrictions, allow access
    if (!item.roles) {
      return true;
    }
    
    // Default: no access if role restrictions exist but user not authenticated
    return false;
  };

  // Helper function to preserve brand parameter during navigation
  const preserveBrandUrl = (href: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    const brandParam = currentParams.get('brand');
    
    if (brandParam) {
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('brand', brandParam);
        return url.pathname + url.search;
      } catch {
        const separator = href.includes('?') ? '&' : '?';
        return `${href}${separator}brand=${encodeURIComponent(brandParam)}`;
      }
    }
    
    return href;
  };

  // Check if current path is active
  const isActivePath = (href: string) => {
    if (href === '/dashboard' && (location === '/' || location === '/dashboard')) return true;
    if (href.includes('?action=create')) {
      // For action URLs, check if the base path matches
      const basePath = href.split('?')[0];
      return location.startsWith(basePath);
    }
    return location === href || location.startsWith(href + '/');
  };

  const navigationItems = getNavigationItems().filter(hasAccess);

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200 safe-area-pb"
      data-testid="bottom-navigation-bar"
    >
      <nav className="flex items-center justify-around h-16 px-2">
        {navigationItems.map((item) => {
          const isActive = isActivePath(item.href);
          const IconComponent = item.icon;
          
          return (
            <Link
              key={item.id}
              href={preserveBrandUrl(item.href)}
              className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-lg transition-all duration-200 active:scale-95 ${
                isActive 
                  ? 'text-primary bg-primary/10' 
                  : 'text-gray-600 hover:text-primary hover:bg-primary/5'
              }`}
              style={{ willChange: 'transform, background-color, color' }}
              data-testid={`bottom-nav-${item.id}`}
            >
              <IconComponent 
                className={`w-5 h-5 mb-1 ${isActive ? 'text-primary' : 'text-gray-600'}`} 
              />
              <span 
                className={`text-xs font-medium leading-tight text-center ${
                  isActive ? 'text-primary' : 'text-gray-600'
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>
      
      {/* Safe area padding for devices with home indicators */}
      <div className="h-safe-area-inset-bottom bg-white/95" />
    </div>
  );
}